import { useTranslation } from '@refinedev/core';
import {
  Button,
  Flex,
  Grid,
  Text,
  Stack,
  useComputedColorScheme,
} from '@mantine/core';
import { IconPlus, IconRepeatOnce } from '@tabler/icons-react';
import {
  ModalTariffSelectColumns,
  TariffStagedAction,
  TariffModalRow,
  TariffRowActions,
} from 'columns/ModalTariffSelect';
import { Debit, isTariff, Tariff } from 'interfaces';
import {
  MRT_RowSelectionState,
  MRT_ShowHideColumnsButton,
  MRT_TableOptions,
} from 'mantine-react-table';
import { useActiveForm } from 'pages/claims/components/ClaimFormProvider';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { buttonTextColor } from 'utils/buttonUtils';
import { getValueByPath } from 'utils/getValueByPath';
import { isResubmittableTariffStatus } from 'utils/claimStatusUtils';
import {
  resolveTariffRequestedAmount,
  shouldApplyHospitalCarePremium,
} from 'utils/tariffUtils';
import { hospitalCarePremium } from 'constants/tariffConstants';
import { BillingDataGridWrapper } from '../BillingDataGridWrapper';
import {
  formatCad,
  getLatestDebitByTariffID,
  RowItem,
} from './utils/tariffHelpers';
import { TariffDetailPanel } from './ModalTariffSelectTableDetailPanel';
import { TariffCommentModal } from './TariffCommentModal';
import { useHotkeys } from '@mantine/hooks';
import { useCurrentSiteStore } from 'store';
import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';

export interface ModalTariffSelectTableProps {
  rows: RowItem[];
  items: RowItem[];
  locationServiceRate: {
    code: string;
    modifier: number;
  };
  debits: Debit[];
  stagedActionByTariffID?: Record<string, TariffStagedAction>;
  isUnreconciled: boolean;
  providerID?: string;
  isAnesthesiaProvider: boolean;
  hospitalCode?: string | null;
  serviceLocationIndicator?: string | null;
  splitOptions: { value: string; label: string }[];
  incisionOptions: { value: string; label: string }[];
  splitLabel: string;
  incisionLabel: string;
  editingTotalId: string | null;
  editingStartTimeId: string | null;
  editingEndTimeId: string | null;
  allocatedTotal: number;
  setEditingTotalId: (id: string | null) => void;
  setEditingStartTimeId: (id: string | null) => void;
  setEditingEndTimeId: (id: string | null) => void;
  changeFn: (stringifiedTariff: string, targetRowID: string) => void;
  handleUnitsChange: (rowID: string, newUnits: number) => void;
  handleAnesthesiaUnitsChange: (rowID: string, newUnits: number) => void;
  handleICD9Change: (rowID: string, icd9: string) => void;
  handleSplitChange: (rowID: string, split: string) => void;
  handleIncisionChange: (rowID: string, incision: string | null) => void;
  onCommentSave: (rowID: string, comment: string) => void;
  handlePercentageChange: (rowID: string, newPct: number | undefined) => void;
  handleStartTimeChange: (rowID: string, timeValue: string) => void;
  handleEndTimeChange: (rowID: string, timeValue: string) => void;
  handleManualTotalSave: (rowID: string, value: number | undefined) => boolean;
  handleManualTotalReset: (rowID: string) => void;
  reconcileTariff: (row: TariffModalRow) => void;
  writeOffTariff: (row: TariffModalRow) => Promise<void>;
  resubmitTariffs: (rows: TariffModalRow[]) => Promise<void>;
  markTariffAs: (row: TariffModalRow, status: string) => Promise<void>;
  addBlankRow: () => string;
  requestDeleteTariff: (row: RowItem, hasPendingResubmission: boolean) => void;
}

const hasSameRowSelection = (
  previous: MRT_RowSelectionState,
  next: MRT_RowSelectionState
) => {
  const previousKeys = Object.keys(previous);
  const nextKeys = Object.keys(next);

  if (previousKeys.length !== nextKeys.length) {
    return false;
  }

  return previousKeys.every((key) => previous[key] === next[key]);
};

const hasValidTariff = (tariff?: Partial<Tariff> | null): tariff is Tariff =>
  !!tariff && isTariff(tariff);

export const ModalTariffSelectTable = memo<ModalTariffSelectTableProps>(
  ({
    rows,
    items,
    locationServiceRate,
    debits,
    stagedActionByTariffID,
    isUnreconciled,
    providerID,
    isAnesthesiaProvider,
    hospitalCode,
    serviceLocationIndicator,
    splitOptions,
    incisionOptions,
    splitLabel,
    incisionLabel,
    allocatedTotal,
    editingTotalId,
    editingStartTimeId,
    editingEndTimeId,
    setEditingTotalId,
    setEditingStartTimeId,
    setEditingEndTimeId,
    changeFn,
    handleUnitsChange,
    handleAnesthesiaUnitsChange,
    handleICD9Change,
    handleSplitChange,
    handleIncisionChange,
    onCommentSave,
    handlePercentageChange,
    handleStartTimeChange,
    handleEndTimeChange,
    handleManualTotalSave,
    handleManualTotalReset,
    reconcileTariff,
    writeOffTariff,
    resubmitTariffs,
    markTariffAs,
    addBlankRow,
    requestDeleteTariff,
  }) => {
    const [commentRowID, setCommentRowID] = useState<string | null>(null);
    const commentRow = rows.find((row) => row._rowID === commentRowID);
    const colorScheme = useComputedColorScheme('dark');

    useHotkeys([['mod+enter', addBlankRow]], [], false);

    const { timezone } = useCurrentSiteStore();
    const { form, prefix } = useActiveForm();
    const rawStatus = getValueByPath(
      form.getValues(),
      prefix ? `${prefix}.status` : 'status'
    );
    const normalizedStatus = rawStatus as string | undefined;

    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
    const getDefaultColumnOrder = useCallback(
      () => [
        'mrt-row-select',
        'mrt-row-expand',
        'code',
        'status',
        'eobs',
        'icd9',
        'units',
        'serviceTime',
        ...(isAnesthesiaProvider ? ['anesthesiaUnits'] : []),
        'percentage',
        ...(!isAnesthesiaProvider ? ['split', 'incision'] : []),
        'total',
        ...(isUnreconciled ? ['paidAmount', 'difference'] : []),
        'mrt-row-actions',
      ],
      [isAnesthesiaProvider, isUnreconciled]
    );
    const [columnOrder, setColumnOrder] = useState<string[]>([
      'mrt-row-select',
      'mrt-row-expand',
      'code',
      'status',
      'eobs',
      'icd9',
      'units',
      'serviceTime',
      ...(isAnesthesiaProvider ? ['anesthesiaUnits'] : []),
      'percentage',
      ...(!isAnesthesiaProvider ? ['split', 'incision'] : []),
      'total',
      ...(isUnreconciled ? ['paidAmount', 'difference'] : []),
      'mrt-row-actions',
    ]);
    const [columnVisibility, setColumnVisibility] = useState<
      Record<string, boolean>
    >({
      code: true,
      status: true,
      eobs: true,
      icd9: true,
      units: true,
      serviceTime: true,
      anesthesiaUnits: isAnesthesiaProvider,
      percentage: true,
      split: !isAnesthesiaProvider,
      incision: !isAnesthesiaProvider,
      rate: false,
      total: true,
      paidAmount: isUnreconciled,
      difference: isUnreconciled,
    });

    useEffect(() => {
      setColumnVisibility((prev) => {
        const next = {
          ...prev,
          paidAmount: isUnreconciled,
          difference: isUnreconciled,
          anesthesiaUnits: isAnesthesiaProvider,
          split: !isAnesthesiaProvider,
          incision: !isAnesthesiaProvider,
        };
        return next;
      });
    }, [isAnesthesiaProvider, isUnreconciled]);

    useEffect(() => {
      setColumnOrder(getDefaultColumnOrder());
    }, [getDefaultColumnOrder]);

    const hasHospitalContext = shouldApplyHospitalCarePremium({
      hospitalCode,
      serviceLocationIndicator,
    });

    const latestDebitByTariffID = useMemo(
      () =>
        getLatestDebitByTariffID(
          debits,
          (debit) => debit.type !== 'RETRACTION'
        ),
      [debits]
    );

    const tableRows: TariffModalRow[] = useMemo(
      () =>
        rows.map((row) => {
          const debit = latestDebitByTariffID.get(row.id);
          const paidValue = Number(debit?.paidAmount ?? 0);
          const hasPaid = !!debit?.paidAmount;
          const requestedAmountStatus =
            normalizedStatus === CLAIM_TARIFF_STATUS.PAID
              ? normalizedStatus
              : row.status ?? normalizedStatus;
          const submittedAmount = resolveTariffRequestedAmount(
            row,
            requestedAmountStatus,
            debit
          );

          const requiresReconciliation =
            hasPaid && paidValue !== submittedAmount;
          return {
            ...row,
            debit,
            stagedAction: stagedActionByTariffID?.[row.id],
            isPendingResubmission: false,
            isResubmitted: false,
            requiresReconciliation,
            hasHospitalCarePremium:
              hasHospitalContext &&
              hospitalCarePremium.has(String(row.code ?? '').trim()),
            difference: Number((paidValue - submittedAmount).toFixed(2)),
            submittedAmount,
          };
        }),
      [
        rows,
        latestDebitByTariffID,
        hasHospitalContext,
        normalizedStatus,
        stagedActionByTariffID,
      ]
    );

    useEffect(() => {
      setRowSelection((prev) => {
        const next = Object.fromEntries(
          Object.entries(prev).filter(([rowID]) =>
            tableRows.some(
              (row) =>
                row._rowID === rowID &&
                hasValidTariff(row) &&
                isResubmittableTariffStatus(row.status)
            )
          )
        );

        return hasSameRowSelection(prev, next) ? prev : next;
      });
    }, [tableRows]);

    const handleResubmitRow = useCallback(
      async (row: TariffModalRow) => {
        if (!hasValidTariff(row) || !isResubmittableTariffStatus(row.status)) {
          return;
        }

        await resubmitTariffs([row]);
      },
      [resubmitTariffs]
    );

    const selectedRows = useMemo(
      () =>
        tableRows.filter(
          (row) =>
            rowSelection[row._rowID] &&
            hasValidTariff(row) &&
            isResubmittableTariffStatus(row.status)
        ),
      [rowSelection, tableRows]
    );

    const selectedTariffIDs = useMemo(
      () => Array.from(new Set(selectedRows.map((row) => row.id))),
      [selectedRows]
    );

    const resubmitSelected = useCallback(async () => {
      if (selectedTariffIDs.length === 0) return;

      await resubmitTariffs(selectedRows);
    }, [resubmitTariffs, selectedRows, selectedTariffIDs.length]);

    const { translate } = useTranslation();
    const eobHeader = translate('pages:claims.tariffEOB.column', 'EOBs');
    const columns = useMemo(
      () =>
        ModalTariffSelectColumns({
          eobHeader,
          items,
          providerID,
          isUnreconciled,
          locationServiceRate,
          isAnesthesiaProvider,
          editingTotalId,
          editingStartTimeId,
          editingEndTimeId,
          setEditingTotalId,
          setEditingStartTimeId,
          setEditingEndTimeId,
          changeFn,
          handleUnitsChange,
          handleAnesthesiaUnitsChange,
          handleICD9Change,
          splitOptions,
          incisionOptions,
          splitLabel,
          incisionLabel,
          handleSplitChange,
          handleIncisionChange,
          handlePercentageChange,
          handleStartTimeChange,
          handleEndTimeChange,
          handleManualTotalSave,
          handleManualTotalReset,
          timezone,
          colorScheme,
        }),
      [
        eobHeader,
        changeFn,
        editingEndTimeId,
        editingStartTimeId,
        editingTotalId,
        handleICD9Change,
        handleAnesthesiaUnitsChange,
        handleEndTimeChange,
        handleManualTotalReset,
        handleManualTotalSave,
        handlePercentageChange,
        handleSplitChange,
        handleIncisionChange,
        splitOptions,
        incisionOptions,
        splitLabel,
        incisionLabel,
        timezone,
        handleStartTimeChange,
        handleUnitsChange,
        isUnreconciled,
        isAnesthesiaProvider,
        items,
        locationServiceRate,
        providerID,
        colorScheme,
        setEditingEndTimeId,
        setEditingStartTimeId,
        setEditingTotalId,
      ]
    );

    const tableOptions = useMemo<MRT_TableOptions<TariffModalRow>>(
      () => ({
        data: tableRows,
        columns,
        enableRowSelection: (row) =>
          hasValidTariff(row.original) &&
          isResubmittableTariffStatus(row.original.status),
        enableMultiRowSelection: true,
        enableExpandAll: false,
        enableExpanding: true,
        enableColumnActions: false,
        enableSorting: false,
        enableFilters: false,
        enableGlobalFilter: false,
        enablePagination: false,
        enableBottomToolbar: false,
        enableTopToolbar: true,
        enableColumnResizing: false,
        enableColumnDragging: true,
        enableColumnOrdering: false,
        enableDensityToggle: true,
        enableFullScreenToggle: false,
        layoutMode: 'grid',
        positionActionsColumn: 'last',
        positionExpandColumn: 'first',
        getRowId: (row) => row._rowID,
        getRowCanExpand: () => true,
        onRowSelectionChange: setRowSelection,
        onColumnVisibilityChange: setColumnVisibility,
        state: {
          rowSelection,
          columnVisibility,
          columnOrder,
        },
        onColumnOrderChange: setColumnOrder,
        mantineSelectAllCheckboxProps: {
          'aria-label': 'Select all tariffs',
          disabled: tableRows.every(
            (row) =>
              !hasValidTariff(row) || !isResubmittableTariffStatus(row.status)
          ),
        },
        mantineSelectCheckboxProps: ({ row }) => ({
          'aria-label': 'Select tariff',
          disabled:
            !hasValidTariff(row.original) ||
            !isResubmittableTariffStatus(row.original.status),
        }),
        mantineTableBodyRowProps: ({ row }) => ({
          style: {
            ...(row.original.isResubmitted
              ? {
                  backgroundColor:
                    colorScheme === 'dark'
                      ? 'rgba(255, 211, 89, 0.14)'
                      : 'rgba(255, 244, 204, 0.8)',
                }
              : undefined),
            width: '100%',
          },
        }),
        mantineTableHeadCellProps: {
          style: {
            justifyContent: 'flex-start',
            textAlign: 'left',
          },
        },
        mantineTableBodyCellProps: {
          style: {
            justifyContent: 'flex-start',
            textAlign: 'left',
          },
        },
        renderDetailPanel: ({ row }) => (
          <TariffDetailPanel
            row={row.original}
            locationServiceRate={locationServiceRate}
          />
        ),
        renderTopToolbarCustomActions: () => {
          const isMac = navigator.platform.includes('Mac');
          return (
            <Flex align="center" gap="sm">
              <Stack gap={4} align="flex-start">
                <Button
                  color="blue"
                  onClick={addBlankRow}
                  variant="outline"
                  c={buttonTextColor(colorScheme, false)}
                  radius="md"
                  leftSection={<IconPlus size={16} />}
                  data-testid="add-tariff-button"
                >
                  Add
                </Button>
                <Text size="xs" c="dimmed">
                  {isMac ? '⌘' : 'Ctrl'} + Enter to add row
                </Text>
              </Stack>
              <Button
                color="yellow"
                variant="light"
                leftSection={<IconRepeatOnce size={16} />}
                disabled={selectedTariffIDs.length === 0}
                onClick={() => void resubmitSelected()}
              >
                Resubmit selected
              </Button>
            </Flex>
          );
        },
        renderToolbarInternalActions: ({ table }) => (
          <MRT_ShowHideColumnsButton table={table} />
        ),
        displayColumnDefOptions: {
          'mrt-row-select': {
            header: '',
            enableHiding: false,
            size: 36,
            minSize: 36,
            maxSize: 36,
            grow: false,
          },
          'mrt-row-expand': {
            header: '',
            enableHiding: false,
            size: 40,
            minSize: 40,
            maxSize: 40,
            grow: false,
          },
          'mrt-row-actions': {
            header: 'Actions',
            enableHiding: false,
            size: 88,
            minSize: 88,
            maxSize: 88,
            grow: false,
            Cell: ({ row }) => (
              <TariffRowActions
                row={row.original}
                isUnreconciled={isUnreconciled}
                onComment={() => setCommentRowID(row.original._rowID)}
                onReconcile={() => reconcileTariff(row.original)}
                onWriteOff={() => writeOffTariff(row.original)}
                onDelete={() =>
                  requestDeleteTariff(
                    row.original as RowItem,
                    row.original.isResubmitted
                  )
                }
                onResubmit={() => handleResubmitRow(row.original)}
                onMarkAs={(status) => markTariffAs(row.original, status)}
              />
            ),
          },
        },
      }),
      [
        addBlankRow,
        colorScheme,
        columnOrder,
        columnVisibility,
        columns,
        handleResubmitRow,
        isUnreconciled,
        isAnesthesiaProvider,
        reconcileTariff,
        writeOffTariff,
        markTariffAs,
        requestDeleteTariff,
        resubmitSelected,
        rowSelection,
        selectedTariffIDs,
        splitOptions,
        incisionOptions,
        splitLabel,
        incisionLabel,
        tableRows,
      ]
    );

    return (
      <>
        {commentRow && (
          <TariffCommentModal
            key={commentRow._rowID}
            row={commentRow}
            onClose={() => setCommentRowID(null)}
            onSave={(comment) => {
              onCommentSave(commentRow._rowID, comment);
              setCommentRowID(null);
            }}
          />
        )}
        <BillingDataGridWrapper
          title="Tariff Selection"
          dataTestID="tariff-table"
          tableOptions={tableOptions}
          showAlertBanner={false}
        />
        <Flex
          mt="sm"
          direction={{ base: 'column', sm: 'row' }}
          align={{ base: 'flex-end', sm: 'center' }}
          justify="space-between"
          gap="sm"
        >
          <Text c="dimmed" size="sm">
            Claim total reflects full amount after modifiers and percentage
            splits.
          </Text>
          <Grid
            w={{ base: '100%', sm: 'auto' }}
            maw={440}
            gutter="xs"
            align="center"
            data-testid="grid"
          >
            <Grid.Col span={12}>
              <Text c="dimmed" ta="right" data-testid="claim-total-title">
                Claim Total
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Text fw={700} ta="right">
                {formatCad(allocatedTotal)}
              </Text>
            </Grid.Col>
          </Grid>
        </Flex>
      </>
    );
  }
);
